import {
  CreateTaskAssignBody,
  GetFilterMetadataResponse,
  GetTaskAssignsResponse,
  GetTasksParams,
  ImportAuditDetail,
  ImportAuditEntry,
  ImportAuditListResponse,
  ShowTaskAssignResponse,
} from '../types/taskAssigns';
import { client } from './index';

function appendMobileUserQueryParams(
  params: URLSearchParams,
  mobileUserIds: number[] | undefined,
  userId: number | undefined,
  useMobileUserIdsQueryParam: boolean | undefined,
) {
  if (useMobileUserIdsQueryParam && mobileUserIds?.length) {
    mobileUserIds.forEach((id) => params.append('mobile_user_ids[]', id.toString()));
    return;
  }
  if (userId != null) params.append('user_id', userId.toString());
}

const getTasks = async ({
  page = 1,
  taskAssignID,
  userId,
  mobileUserIds,
  useMobileUserIdsQueryParam,
  locationId,
  locationIds,
  locationType,
  taskFormId,
  taskFormIds,
  states,
  state,
  startDate,
  endDate,
  sortBy,
  order,
  searchFormName,
  assetId,
  assetTypeIds,
}: GetTasksParams) => {
  const params = new URLSearchParams();

  if (page) params.append('page', page.toString());
  if (taskAssignID) params.append('assignID', taskAssignID);
  appendMobileUserQueryParams(params, mobileUserIds, userId, useMobileUserIdsQueryParam);
  if (locationIds && locationIds.length > 0) {
    locationIds.forEach((id) => params.append('location_ids[]', id.toString()));
  } else if (locationId) {
    params.append('location_id', locationId.toString());
  }
  if (locationType) params.append('location_type', locationType);
  if (taskFormIds?.length) {
    taskFormIds.forEach((id) => params.append('task_form_ids[]', id.toString()));
  } else if (taskFormId) {
    params.append('task_form_id', taskFormId.toString());
  }
  if (states?.length) {
    states.forEach((value) => params.append('states[]', value));
  } else if (state) {
    params.append('state', state);
  }
  if (startDate) params.append('start', startDate);
  if (endDate) params.append('end', endDate);
  if (sortBy) params.append('sort_by', sortBy);
  if (order) params.append('order', order);
  if (searchFormName) params.append('search_form_name', searchFormName);
  if (assetId) params.append('asset_id', assetId);
  if (assetTypeIds) params.append('asset_type_ids', assetTypeIds);

  const response = await client.get<GetTaskAssignsResponse>(
    `/task_assigns_react?${params.toString()}`,
  );
  const { data } = response;
  return data;
};

const getTasksBoxes = async ({
  page = 1,
  taskAssignID,
  userId,
  mobileUserIds,
  useMobileUserIdsQueryParam,
  locationId,
  locationIds,
  locationType,
  taskFormId,
  taskFormIds,
  states,
  state,
  startDate,
  endDate,
  sortBy,
  order,
  searchFormName,
  assetId,
  assetTypeIds,
}: GetTasksParams) => {
  const params = new URLSearchParams();

  if (page) params.append('page', page.toString());
  if (taskAssignID) params.append('assignID', taskAssignID);
  appendMobileUserQueryParams(params, mobileUserIds, userId, useMobileUserIdsQueryParam);
  if (locationIds && locationIds.length > 0) {
    locationIds.forEach((id) => params.append('location_ids[]', id.toString()));
  } else if (locationId) {
    params.append('location_id', locationId.toString());
  }
  if (locationType) params.append('location_type', locationType);
  if (taskFormIds?.length) {
    taskFormIds.forEach((id) => params.append('task_form_ids[]', id.toString()));
  } else if (taskFormId) {
    params.append('task_form_id', taskFormId.toString());
  }
  if (states?.length) {
    states.forEach((value) => params.append('states[]', value));
  } else if (state) {
    params.append('state', state);
  }
  if (startDate) params.append('start', startDate);
  if (endDate) params.append('end', endDate);
  if (sortBy) params.append('sort_by', sortBy);
  if (order) params.append('order', order);
  if (searchFormName) params.append('search_form_name', searchFormName);
  if (assetId) params.append('asset_id', assetId);
  if (assetTypeIds) params.append('asset_type_ids', assetTypeIds);

  const response = await client.get<GetTaskAssignsResponse>(
    `/task_assigns_react_boxes?${params.toString()}`,
  );
  const { data } = response;
  return data;
};

const getLightFilterMetadata = async (options?: { includeDeactivatedUsers?: boolean }) => {
  const params = new URLSearchParams();
  if (options?.includeDeactivatedUsers) {
    params.append('include_deactivated_users', 'true');
  }
  const query = params.toString();
  const url = query ? `/filter_metadata?${query}` : '/filter_metadata';
  const response = await client.get<GetFilterMetadataResponse>(url);
  const { data } = response;
  return data;
};

interface CreateTaskAssignResponse {
  task_assign_id?: number;
  assign_id?: string;
}

const createTaskAssign = async (taskAssign: CreateTaskAssignBody): Promise<CreateTaskAssignResponse> => {
  const response = await client.post<CreateTaskAssignResponse>('/task_assigns_create', taskAssign);
  return response.data ?? {};
};

const updateTaskAssign = async (taskId: number, taskAssign: CreateTaskAssignBody) => {
  const response = await client.put(`/task_assign_update/${taskId}`, taskAssign, {
    headers: {
      accept: 'application/json',
    },
  });
  const { data } = response;
  return data;
};

const notifyUsers = async (taskID: number) => {
  const response = await client.post('/tasks_notify_react.json', { task_id: taskID });
  const { data } = response;
  return data;
};

const deleteTask = async (taskID: number) => {
  const response = await client.delete(`/task_assigns_delete/${taskID}`);
  const { data } = response;
  return data;
};

const getEditInfo = async (taskID: number) => {
  const response = await client.get(`/task_assign_edit/${taskID}`);
  const { data } = response;
  return data;
};

type BulkDeleteTasksResponse = {
  result: string;
  // Optional to remain resilient against a deploy window where the rails side
  // has not yet shipped the new response shape, or an older client gets routed
  // to a newer rails revision.
  deleted_ids?: number[];
  skipped_ids?: number[];
};

const bulkDeleteTasks = async (selectedTaskIds: number[]): Promise<BulkDeleteTasksResponse> => {
  const response = await client.post<BulkDeleteTasksResponse>('/tasks_bulk_delete_react', {
    form_ids: selectedTaskIds,
  });
  return response.data;
};

const bulkNotifyUsers = async (selectedTaskIds: number[]) => {
  const response = await client.post('/bulk_notify_react', { form_ids: selectedTaskIds });
  const { data } = response;
  return data;
};

const updateTask = async (
  taskID: string,
  task: CreateTaskAssignBody['task_assign'],
  time: CreateTaskAssignBody['time'],
  todoValue: string,
) => {
  const taskAssign = {
    task_assign: task,
    time,
    'todo-value': todoValue,
  };
  const response = await client.put(`/task_assign_update/${taskID}`, taskAssign);
  const { data } = response;
  return data;
};

const updateTaskAssignAcceptance = async (taskID: number, acceptance: boolean) => {
  const response = await client.patch(`/task_assign_acceptance`, {
    accept: acceptance ? 'true' : 'false',
    task_assign_id: taskID,
  });
  const { data } = response;
  return data;
};

const getOldVersion = async () => {
  const response = await client.get('/old_task_assigner');
  const { data } = response;
  return data;
};

export interface locationInfo {
  value: string;
  label: string;
}

export const loadOptions = async (
  searchQuery: string,
  loadedOptions: locationInfo[],
  startId = 0,
) => {
  const params = new URLSearchParams();

  const lastId = loadedOptions.length > 0 ? loadedOptions[loadedOptions.length - 1].value : startId;
  params.append('start', lastId.toString());
  if (searchQuery != '') params.append('search', searchQuery);

  const request_url = `/filter_location?${params.toString()}`;
  const response = await client.get(request_url);

  const locationList = response.data.locations.map((entry: (string | number)[]) => ({
    value: entry[1].toString(),
    label: entry[0],
  }));

  return {
    options: locationList, // cada vez que se cumple hasMore, se agregan estos elementos
    hasMore: locationList.length >= 50,
  };
};

export const getTaskAssign = async (taskAssignId: number): Promise<ShowTaskAssignResponse> => {
  const response = await client.get(`/task_assigns/${taskAssignId}`);
  const { data } = response;
  return data;
};

const importTasks = async (file: File) => {
  const formData = new FormData();
  formData.append('import[file]', file);

  const response = await client.post<{ message?: string; error?: string; audit_log_id?: number }>(
    '/import_tasks_react',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
    },
  );
  return response.data;
};

const getImportAudits = async (page = 1): Promise<ImportAuditListResponse> => {
  const response = await client.get(`/task_assigns_react/import_audits?page=${page}`);
  return response.data;
};

const getImportAudit = async (id: number): Promise<ImportAuditDetail> => {
  const response = await client.get(`/task_assigns_react/import_audits/${id}`);
  return response.data;
};

const resolveImportAuditEntry = async (
  auditLogId: number,
  entryId: number,
  taskAssignId?: number,
): Promise<{ success: boolean; entry?: ImportAuditEntry }> => {
  const response = await client.post(
    `/task_assigns_react/import_audits/${auditLogId}/entries/${entryId}/resolve`,
    taskAssignId ? { task_assign_id: taskAssignId } : {},
  );
  return response.data;
};

export const taskAssignsApi = {
  getLightFilterMetadata,
  notifyUsers,
  deleteTask,
  getEditInfo,
  createTaskAssign,
  updateTaskAssign,
  getTasks,
  bulkDeleteTasks,
  bulkNotifyUsers,
  updateTask,
  getOldVersion,
  getTasksBoxes,
  loadOptions,
  updateTaskAssignAcceptance,
  getTaskAssign,
  importTasks,
  getImportAudits,
  getImportAudit,
  resolveImportAuditEntry,
};
